import React, { useState, useEffect } from 'react';
import { X, Package, Search, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { getConsumables } from '@/pages/parametrizacion/consumables/Services/consumables.services';
import { assignInsumosToEdificio, getEdificioInsumos } from '@/services/edificioInsumos.service';

const AssignInsumosModal = ({ isOpen, onClose, edificio, onSuccess }) => {
  const { toast } = useToast();
  const [insumos, setInsumos] = useState([]);
  const [selectedInsumos, setSelectedInsumos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && edificio) {
      loadData();
    }
  }, [isOpen, edificio]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Cargar todos los insumos activos
      const insumosResponse = await getConsumables({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' }
      });

      setInsumos(insumosResponse.data || []);

      // Cargar insumos ya asignados al edificio
      const assignedResponse = await getEdificioInsumos(edificio._id || edificio.id);
      setSelectedInsumos(assignedResponse.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los insumos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleInsumo = (insumoId) => {
    setSelectedInsumos(prev => {
      if (prev.includes(insumoId)) {
        return prev.filter(id => id !== insumoId);
      } else {
        return [...prev, insumoId];
      }
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await assignInsumosToEdificio(edificio._id || edificio.id, selectedInsumos);
      
      toast({
        title: "Éxito",
        description: "Insumos asignados correctamente",
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error al asignar insumos:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudieron asignar los insumos",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredInsumos = insumos.filter(insumo =>
    insumo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    insumo.tipo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Package className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Asignar Insumos
              </h2>
              <p className="text-sm text-gray-500">
                {edificio?.nombre}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar insumos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Selected Count */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {selectedInsumos.length} insumo(s) seleccionado(s)
            </p>
            {selectedInsumos.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedInsumos([])}
              >
                Limpiar selección
              </Button>
            )}
          </div>

          {/* Insumos List */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Cargando insumos...</p>
            </div>
          ) : filteredInsumos.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No se encontraron insumos</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredInsumos.map((insumo) => {
                const isSelected = selectedInsumos.includes(insumo._id);
                return (
                  <div
                    key={insumo._id}
                    onClick={() => handleToggleInsumo(insumo._id)}
                    className={`
                      p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`
                          p-2 rounded-lg
                          ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}
                        `}>
                          <Package className={`h-4 w-4 ${isSelected ? 'text-purple-600' : 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{insumo.nombre}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            {insumo.tipo && (
                              <Badge variant="outline" className="text-xs">
                                {insumo.tipo}
                              </Badge>
                            )}
                            {insumo.descripcion && (
                              <p className="text-xs text-gray-500 truncate">
                                {insumo.descripcion}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="ml-3">
                          <div className="h-6 w-6 bg-purple-600 rounded-full flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading}
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignInsumosModal;
